import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Code, RefreshCw, CheckCircle, AlertCircle, Loader, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AdminPages.css';

const APILogs = () => {
  const location = useLocation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, success, failed
  const [currentPage, setCurrentPage] = useState(1);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const itemsPerPage = 15;

  // Determine pipeline based on current route
  const isV1Pipeline = location.pathname.startsWith('/v1');
  const pipelineFilter = isV1Pipeline ? 'v1' : 'main';

  const isSuperAdmin = currentUserRole === 'superadmin';

  const [totalStats, setTotalStats] = useState({ success: 0, failed: 0 });

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const url = `/api/logs/api?page=${page}&limit=${itemsPerPage}&status=${statusFilter}&pipeline=${pipelineFilter}`;
      const data = await apiCall(url);

      // Handle new paginated response format
      if (data.data && data.pagination) {
        setLogs(data.data);
        setCurrentPage(data.pagination.page);

        // Update total stats from backend
        if (data.stats) {
          setTotalStats(data.stats);
        }
      } else {
        // Fallback for old format
        setLogs(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching API logs:', err);
      setError('Failed to load API logs');
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async (days) => {
    try {
      setClearing(true);
      const data = await apiCall('/api/logs/api/clear', {
        method: 'POST',
        body: JSON.stringify({ days })
      });

      if (data && data.message) {
        setSuccess(`✓ ${data.message}`);
        setConfirmClear(null);
        fetchLogs(1);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(data?.error || 'Failed to clear logs');
      }
    } catch (err) {
      console.error('Error clearing logs:', err);
      setError('Failed to clear logs');
    } finally {
      setClearing(false);
    }
  };

  useMountEffect(() => {
    // Get current user role on mount and fetch logs only if authenticated
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      setError('Please login to view logs');
      setLoading(false);
      return;
    }

    const user = JSON.parse(userData);
    setCurrentUserRole(user.role);
    fetchLogs(currentPage);
  });

  useEffect(() => {
    // Only fetch logs if user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      fetchLogs(currentPage);
    }
  }, [currentPage, statusFilter]);

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1><Code size={28} /> {isV1Pipeline ? 'V1 ' : ''}API Logs</h1>
        </div>
        <div className="form-section" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader size={32} className="spinner" style={{ margin: '0 auto', color: '#3b82f6' }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading API logs...</p>
        </div>
      </div>
    );
  }

  const isSuccess = (status) => status === 200;
  const isFailed = (status) => status !== 200;

  // Filter logs by search term (already filtered by status on backend)
  const displayedLogs = logs.filter(log =>
    log.endpoint.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.method.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Code size={28} /> {isV1Pipeline ? 'V1 ' : ''}API Logs</h1>
          <p>Monitor and analyze {isV1Pipeline ? 'V1 pipeline ' : ''}API requests</p>
        </div>
        {!isSuperAdmin && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: '#f3f4f6',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            🔒 Read Only
          </div>
        )}
      </div>

      {error && (
        <div className="status-message error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="close-btn">✕</button>
        </div>
      )}

      {success && (
        <div className="status-message success">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="close-btn">✕</button>
        </div>
      )}

      <div className="form-section">
        <div className="section-header">
          <h3><Code size={20} /> Logs</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search endpoint or method..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                minWidth: '250px'
              }}
            />
            <button className="btn btn-secondary btn-sm" onClick={fetchLogs} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All Logs ({totalStats.success + totalStats.failed})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'success' ? 'active' : ''}`}
            onClick={() => setStatusFilter('success')}
            style={{
              borderColor: totalStats.success > 0 ? '#10b981' : '#e2e8f0',
              color: statusFilter === 'success' ? 'white' : '#10b981'
            }}
          >
            <CheckCircle size={14} /> Success ({totalStats.success})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'failed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('failed')}
            style={{
              borderColor: totalStats.failed > 0 ? '#ef4444' : '#e2e8f0',
              color: statusFilter === 'failed' ? 'white' : '#ef4444'
            }}
          >
            <AlertCircle size={14} /> Failed ({totalStats.failed})
          </button>
        </div>

        {isSuperAdmin && (
          <div style={{ marginBottom: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Clear Old Logs:</span>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 1, label: 'yesterday' })}
              disabled={clearing}
              title="Clear logs from yesterday"
            >
              <Trash2 size={12} />
              <span>Yesterday</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 7, label: 'last 7 days' })}
              disabled={clearing}
              title="Clear logs from last 7 days"
            >
              <Trash2 size={12} />
              <span>7 Days</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 15, label: 'last 15 days' })}
              disabled={clearing}
              title="Clear logs from last 15 days"
            >
              <Trash2 size={12} />
              <span>15 Days</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 30, label: 'last 30 days' })}
              disabled={clearing}
              title="Clear logs from last 30 days"
            >
              <Trash2 size={12} />
              <span>Month</span>
            </button>
          </div>
        )}

        {displayedLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Code size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No logs found</p>
            <p style={{ fontSize: '14px' }}>Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="collections-table">
              <table>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>IP Address</th>
                    <th>Status</th>
                    <th>Response Time</th>
                    <th>Created By</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedLogs.map(log => (
                    <tr key={log.id}>
                      <td>
                        <span className={`method-badge ${log.method.toLowerCase()}`}>
                          {log.method}
                        </span>
                      </td>
                      <td style={{ fontSize: '13px', fontFamily: 'monospace' }}>{log.endpoint}</td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>{log.ipAddress}</td>
                      <td>
                        <span className={`status-badge status-${isSuccess(log.status) ? 'success' : isFailed(log.status) ? 'error' : 'info'}`}>
                          {log.status}
                        </span>
                      </td>
                      <td>{log.time}</td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>{log.createdBy}</td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>{log.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                Showing {displayedLogs.length} of total logs (Page {currentPage})
              </span>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={14} />
                </button>

                <span style={{ fontSize: '12px', color: '#1e293b', fontWeight: '600', minWidth: '60px', textAlign: 'center' }}>
                  Page {currentPage}
                </span>

                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={displayedLogs.length < itemsPerPage}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {confirmClear && (
        <div className="modal-overlay" onClick={() => setConfirmClear(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>⚠️ Clear Logs</h3>
            <p>
              This will permanently delete all logs from the {confirmClear.label}. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmClear(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleClearLogs(confirmClear.days)}
                disabled={clearing}
              >
                {clearing ? (
                  <>
                    <Loader size={14} className="spinner" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} /> Delete Logs
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APILogs;
